import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Star, Send, User, Calendar, Truck, CheckCircle,
  Quote, ThumbsUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import VideoHero from "@/components/VideoHero";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarRating = ({ rating, setRating, interactive = false, size = "w-6 h-6" }) => {
  const [hoverRating, setHoverRating] = useState(0);
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type={interactive ? "button" : undefined}
          onClick={() => interactive && setRating(star)}
          onMouseEnter={() => interactive && setHoverRating(star)}
          onMouseLeave={() => interactive && setHoverRating(0)}
          className={interactive ? "cursor-pointer transition-transform hover:scale-110" : "cursor-default"}
          disabled={!interactive}
        >
          <Star
            className={`${size} ${
              star <= (hoverRating || rating)
                ? "fill-yellow-400 text-yellow-400"
                : "text-slate-300"
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const ReviewCard = ({ review }) => {
  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="bg-[#0047AB]/10 w-12 h-12 rounded-full flex items-center justify-center">
            <User className="w-6 h-6 text-[#0047AB]" />
          </div>
          <div>
            <h4 className="font-bold text-slate-900">{review.customer_name}</h4>
            <p className="text-sm text-slate-500">{formatDate(review.created_at)}</p>
          </div>
        </div>
        <StarRating rating={review.rating} size="w-5 h-5" />
      </div>
      
      <h3 className="font-bold text-lg text-slate-800 mb-2">{review.title}</h3>
      <p className="text-slate-600 leading-relaxed mb-4">"{review.review_text}"</p>
      
      {review.service_type && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Truck className="w-4 h-4" />
          <span>{review.service_type.charAt(0).toUpperCase() + review.service_type.slice(1)} Move</span>
        </div>
      )}
    </div>
  );
};

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ total_reviews: 0, average_rating: 0, rating_distribution: {} });
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    customer_name: "",
    email: "",
    rating: 5,
    title: "",
    review_text: "",
    move_date: "",
    service_type: ""
  });

  useEffect(() => {
    fetchReviews();
    fetchStats();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data.reviews || []);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/reviews/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.title || !formData.review_text) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/reviews`, {
        ...formData,
        email: formData.email || undefined
      });
      setSubmitted(true);
      toast.success("Thank you for your review! It will be published after approval.");
    } catch (error) {
      console.error("Review submission error:", error);
      toast.error("Failed to submit review. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="reviews-page">
      {/* Hero Section with Video */}
      <VideoHero
        title="Customer Reviews"
        subtitle="See what our customers say about their moving experience with Tony Moves It."
      />

      {/* Stats Section */}
      <section className="py-12 bg-[#0047AB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center text-white">
            <div>
              <div className="text-5xl font-bold font-['Barlow_Condensed'] mb-2">
                {stats.average_rating > 0 ? stats.average_rating.toFixed(1) : "5.0"}
              </div>
              <StarRating rating={Math.round(stats.average_rating || 5)} size="w-6 h-6" />
              <p className="text-white/80 mt-2">Average Rating</p>
            </div>
            <div>
              <div className="text-5xl font-bold font-['Barlow_Condensed'] mb-2">
                {stats.total_reviews || "0"}+
              </div>
              <p className="text-white/80">Happy Customers</p>
            </div>
            <div>
              <div className="text-5xl font-bold font-['Barlow_Condensed'] mb-2">
                100%
              </div>
              <p className="text-white/80">Satisfaction Guaranteed</p>
            </div>
          </div>
        </div>
      </section>

      <div className="bg-[#F8FAFC] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Reviews List */}
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold font-['Barlow_Condensed'] text-slate-900 uppercase mb-8">
                What Our Customers Say
              </h2>
              
              {isLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin w-8 h-8 border-4 border-[#0047AB] border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-slate-500">Loading reviews...</p>
                </div>
              ) : reviews.length > 0 ? (
                <div className="space-y-6">
                  {reviews.map((review) => (
                    <ReviewCard key={review.id} review={review} />
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
                  <Quote className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-slate-700 mb-2">No Reviews Yet</h3>
                  <p className="text-slate-500 mb-6">Be the first to share your experience!</p>
                </div>
              )}
            </div>

            {/* Review Form */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sticky top-24">
                {submitted ? (
                  <div className="text-center py-8">
                    <div className="bg-green-50 border border-green-200 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Thank You!</h3>
                    <p className="text-slate-600 mb-6">
                      Your review has been submitted and will appear after approval.
                    </p>
                    <Button 
                      onClick={() => {
                        setSubmitted(false);
                        setFormData({
                          customer_name: "",
                          email: "",
                          rating: 5,
                          title: "",
                          review_text: "",
                          move_date: "",
                          service_type: ""
                        });
                      }}
                      variant="outline"
                    >
                      Write Another Review
                    </Button>
                  </div>
                ) : (
                  <>
                    <h3 className="text-xl font-bold font-['Barlow_Condensed'] text-slate-900 uppercase mb-6 flex items-center gap-2">
                      <ThumbsUp className="w-5 h-5 text-[#0047AB]" />
                      Share Your Experience
                    </h3>
                    
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="customer_name">Your Name *</Label>
                        <Input
                          id="customer_name"
                          name="customer_name"
                          value={formData.customer_name}
                          onChange={handleInputChange}
                          placeholder="John Smith"
                          className="mt-1"
                          required
                          data-testid="review-name-input"
                        />
                      </div>

                      <div>
                        <Label htmlFor="email">Email (optional)</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="john@example.com"
                          className="mt-1"
                          data-testid="review-email-input"
                        />
                      </div>

                      <div>
                        <Label>Your Rating *</Label>
                        <div className="mt-2">
                          <StarRating 
                            rating={formData.rating} 
                            setRating={(r) => setFormData(prev => ({ ...prev, rating: r }))}
                            interactive={true}
                            size="w-8 h-8"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="service_type">Service Type</Label>
                        <Select 
                          value={formData.service_type} 
                          onValueChange={(v) => setFormData(prev => ({ ...prev, service_type: v }))}
                        >
                          <SelectTrigger className="mt-1" data-testid="service-type-select">
                            <SelectValue placeholder="Select service type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="residential">Residential Move</SelectItem>
                            <SelectItem value="commercial">Commercial Move</SelectItem>
                            <SelectItem value="specialty">Specialty Items</SelectItem>
                            <SelectItem value="loading">Loading/Unloading Only</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="title">Review Title *</Label>
                        <Input
                          id="title"
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          placeholder="Great experience!"
                          className="mt-1"
                          required
                          data-testid="review-title-input"
                        />
                      </div>

                      <div>
                        <Label htmlFor="review_text">Your Review *</Label>
                        <Textarea
                          id="review_text"
                          name="review_text"
                          value={formData.review_text}
                          onChange={handleInputChange}
                          placeholder="Tell us about your moving experience..."
                          className="mt-1 min-h-[120px]"
                          required
                          data-testid="review-text-input"
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full bg-[#0047AB] hover:bg-[#003380] text-white font-bold"
                        disabled={isSubmitting}
                        data-testid="submit-review-btn"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Submit Review
                          </>
                        )}
                      </Button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewsPage;
